import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import {
  Paperclip,
  Upload,
  FileText,
  Image,
  Trash2,
  Download,
  Eye,
  X,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { NeuCard } from '@/components/ui/neu-card';
import { NeuButton } from '@/components/ui/neu-button';
import { NeuSelect } from '@/components/ui/neu-select';
import { DeleteAdjuntoConfirmModal } from '@/components/domain/delete-adjunto-confirm-modal';
import {
  useAdjuntos,
  useAdjuntoPresign,
  useAdjuntoConfirm,
  useDeleteAdjunto,
  adjuntoKeys,
} from '@/api/tickets';
import { ApiClientError } from '@/api/client';
import type { AdjuntoWithUrl, AdjuntoTipo, AdjuntoMimeType } from '@sime/shared';
import { ADJUNTO_MIME_TYPES, ADJUNTO_MAX_FILE_SIZE, ADJUNTO_TIPOS } from '@sime/shared';

interface AdjuntosTabProps {
  ticketId: number;
  readonly?: boolean;
}

/** Human-readable file size */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Human-readable tipo label */
function tipoLabel(tipo: string): string {
  const labels: Record<string, string> = {
    certificado: 'Certificado',
    respaldo: 'Respaldo',
    foto_adjunta: 'Foto',
  };
  return labels[tipo] ?? tipo;
}

/** Get file icon based on mime type */
function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) {
    return <Image className="h-5 w-5 text-sime-accent" />;
  }
  return <FileText className="h-5 w-5 text-sime-danger" />;
}

export function AdjuntosTab({ ticketId, readonly = false }: AdjuntosTabProps) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useAdjuntos(ticketId);
  const presignMutation = useAdjuntoPresign(ticketId);
  const confirmMutation = useAdjuntoConfirm(ticketId);
  const deleteMutation = useDeleteAdjunto(ticketId);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedTipo, setSelectedTipo] = useState<AdjuntoTipo>('certificado');
  const [preview, setPreview] = useState<{ url: string; mimeType: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const adjuntos = data?.data ?? [];

  const handleFileSelect = useCallback(async (file: File) => {
    setUploadError(null);

    // Validate file type
    if (!ADJUNTO_MIME_TYPES.includes(file.type as AdjuntoMimeType)) {
      setUploadError('Tipo de archivo no permitido. Use PDF, JPG, PNG, WebP o DOCX.');
      return;
    }

    // Validate file size
    if (file.size > ADJUNTO_MAX_FILE_SIZE) {
      setUploadError(`El archivo excede el tamaño maximo de ${ADJUNTO_MAX_FILE_SIZE / (1024 * 1024)} MB.`);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress('Generando URL de subida...');

      // 1. Get presigned URL
      const presignResult = await presignMutation.mutateAsync({
        fileName: file.name,
        mimeType: file.type as AdjuntoMimeType,
        fileSize: file.size,
        tipo: selectedTipo,
      });

      const { uploadUrl, s3Key } = presignResult.data;

      // 2. Upload to S3 directly
      setUploadProgress('Subiendo archivo...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Error al subir el archivo: ${uploadResponse.status}`);
      }

      // 3. Confirm upload
      setUploadProgress('Confirmando...');
      await confirmMutation.mutateAsync({
        s3Key,
        fileName: file.name,
        mimeType: file.type as AdjuntoMimeType,
        fileSize: file.size,
        tipo: selectedTipo,
      });

      setUploadProgress(null);
      setUploadError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al subir el archivo';
      setUploadError(message);
      setUploadProgress(null);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [presignMutation, confirmMutation, selectedTipo]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDelete = useCallback(async (adjuntoId: number) => {
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(adjuntoId);
      setDeleteConfirm(null);
    } catch (err) {
      if (err instanceof ApiClientError) {
        // 404: el adjunto ya no existe en BD (borrado por otra pestana / refresh stale).
        // No es un error real: refrescamos la lista y cerramos el prompt.
        if (err.statusCode === 404) {
          void queryClient.invalidateQueries({ queryKey: adjuntoKeys.all(ticketId) });
          setDeleteConfirm(null);
          return;
        }
        const body = err.body as { error?: { message?: string } } | undefined;
        const serverMessage = body?.error?.message;
        if (err.statusCode === 409) {
          // Estado del ticket cambio entre el render y el delete. Refrescar lista tambien
          // ayuda a que la UI refleje el nuevo estado (probablemente no-INICIADO).
          void queryClient.invalidateQueries({ queryKey: adjuntoKeys.all(ticketId) });
          setDeleteError(
            serverMessage
              ?? 'El ticket cambio de estado. Recarga la pagina y vuelve a intentar.',
          );
        } else {
          setDeleteError(serverMessage ?? `Error ${String(err.statusCode)}: no se pudo eliminar.`);
        }
      } else {
        setDeleteError('Error de red al eliminar. Verifica tu conexion e intenta de nuevo.');
      }
    }
  }, [deleteMutation, queryClient, ticketId]);

  // Loading skeleton
  if (isLoading) {
    return (
      <NeuCard>
        <div className="flex items-center gap-2 mb-4">
          <Paperclip className="h-4 w-4 text-sime-text-tertiary" />
          <h3 className="text-[15px] font-semibold text-sime-text-primary">
            Certificados y Respaldos
          </h3>
        </div>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 bg-sime-surface-raised rounded-xl animate-pulse" />
          ))}
        </div>
      </NeuCard>
    );
  }

  return (
    <div className="space-y-6">
      <NeuCard>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-sime-text-tertiary" />
            <h3 className="text-[15px] font-semibold text-sime-text-primary tracking-tight-xs">
              Certificados y Respaldos
            </h3>
            {adjuntos.length > 0 ? (
              <span className="text-xs text-sime-text-tertiary">
                ({adjuntos.length})
              </span>
            ) : null}
          </div>
        </div>

        {/* Upload Area (only when editable) */}
        {!readonly ? (
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-3">
              <NeuSelect
                options={ADJUNTO_TIPOS.map((tipo) => ({ value: tipo, label: tipoLabel(tipo) }))}
                value={selectedTipo}
                onChange={(v) => { setSelectedTipo(v as AdjuntoTipo); }}
                aria-label="Tipo de adjunto"
              />

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.docx"
                onChange={handleInputChange}
                className="hidden"
                aria-label="Seleccionar archivo para adjuntar"
              />

              <NeuButton
                variant="secondary"
                size="sm"
                onClick={() => { fileInputRef.current?.click(); }}
                disabled={uploading}
                loading={uploading}
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                {uploading ? 'Subiendo...' : 'Adjuntar archivo'}
              </NeuButton>
            </div>

            {uploadProgress ? (
              <div className="flex items-center gap-2 text-xs text-sime-text-secondary mb-2">
                <div className="h-1 w-24 bg-sime-surface-raised rounded-full overflow-hidden">
                  <div className="h-full bg-sime-accent rounded-full animate-pulse w-2/3" />
                </div>
                {uploadProgress}
              </div>
            ) : null}

            {uploadError ? (
              <div className="flex items-center gap-2 p-2 rounded-xl bg-sime-danger/10 border border-sime-danger/20 text-sm text-sime-danger mb-2">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {uploadError}
              </div>
            ) : null}

            <p className="text-xs text-sime-text-tertiary">
              Formatos: PDF, JPG, PNG, WebP, DOCX. Max 10 MB por archivo.
            </p>
          </div>
        ) : null}

        {/* Delete Error Banner */}
        {deleteError ? (
          <div
            role="alert"
            className="flex items-start gap-2 p-3 mb-3 rounded-xl bg-sime-danger/10 border border-sime-danger/20 text-sm text-sime-danger"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium">No se pudo eliminar el archivo</p>
              <p className="text-xs mt-0.5 opacity-90">{deleteError}</p>
            </div>
            <button
              onClick={() => { setDeleteError(null); }}
              aria-label="Cerrar aviso de error"
              className="shrink-0 p-0.5 rounded hover:bg-sime-danger/10 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}

        {/* Adjuntos List */}
        {adjuntos.length === 0 ? (
          <div className="text-center py-8">
            <Paperclip className="h-8 w-8 mx-auto mb-2 text-sime-text-tertiary opacity-50" />
            <p className="text-sm text-sime-text-tertiary">
              Sin certificados adjuntos
            </p>
            {!readonly ? (
              <p className="text-xs text-sime-text-tertiary mt-1">
                Use el boton &quot;Adjuntar archivo&quot; para agregar certificados o respaldos.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-2">
            {adjuntos.map((adjunto) => (
              <AdjuntoItem
                key={adjunto.id}
                adjunto={adjunto}
                readonly={readonly}
                onPreview={(adj) => { setPreview({ url: adj.url, mimeType: adj.mimeType }); }}
                onDeleteRequest={() => { setDeleteConfirm(adjunto.id); setDeleteError(null); }}
              />
            ))}
          </div>
        )}
      </NeuCard>

      {/* Delete Confirmation Modal */}
      <DeleteAdjuntoConfirmModal
        open={deleteConfirm !== null}
        fileName={adjuntos.find((a) => a.id === deleteConfirm)?.nombre ?? ''}
        pending={deleteMutation.isPending}
        onClose={() => { setDeleteConfirm(null); setDeleteError(null); }}
        onConfirm={() => { if (deleteConfirm !== null) void handleDelete(deleteConfirm); }}
      />

      {/* Preview Modal */}
      <AnimatePresence>
        {preview ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-sime-modal-backdrop flex flex-col"
            style={{ background: 'rgba(0,0,0,0.85)' }}
          >
            <div className="flex items-center justify-end px-4 py-3 bg-sime-surface shadow-md shrink-0">
              <div className="flex items-center gap-2">
                {/* Cross-origin presigned URLs ignore the `download` attribute, so
                    this opens the document in a new tab rather than promising a
                    download it can't deliver. The list row keeps a real download. */}
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-sime-accent text-white hover:opacity-90 transition-opacity"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Abrir en pestana nueva
                </a>
                <button
                  onClick={() => { setPreview(null); }}
                  aria-label="Cerrar vista previa"
                  className="p-2 rounded-lg text-sime-text-secondary hover:text-sime-text-primary transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <DocumentPreview url={preview.url} mimeType={preview.mimeType} />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document Preview — renders the in-modal viewer (desktop). Images use <img>;
// PDFs use an <iframe> with an error fallback. Mobile never reaches the PDF
// iframe here: the list routes mobile PDF taps to a new tab (AdjuntoItem),
// because iOS Safari and Android Chrome cannot render PDFs inside an iframe.
// ---------------------------------------------------------------------------

function DocumentPreview({ url, mimeType }: { url: string; mimeType: string }) {
  const [pdfError, setPdfError] = useState(false);
  const [src, setSrc] = useState(url);

  if (mimeType.startsWith('image/')) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4">
        <img
          src={url}
          alt="Vista previa"
          className="max-w-full max-h-full object-contain rounded-lg"
        />
      </div>
    );
  }

  const handleRetry = () => {
    // Cache-buster forces a fresh fetch in case the presigned URL expired.
    const sep = url.includes('?') ? '&' : '?';
    setSrc(`${url}${sep}_r=${Date.now()}`);
    setPdfError(false);
  };

  if (pdfError) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 text-white text-center p-6">
        <FileText className="h-12 w-12 opacity-50" />
        <p className="text-lg font-medium">No se pudo cargar el documento</p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={handleRetry}
            className="px-5 py-2.5 rounded-xl bg-sime-accent text-white font-medium hover:opacity-90 transition-opacity"
          >
            Reintentar
          </button>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Abrir en pestana nueva
          </a>
        </div>
      </div>
    );
  }

  return (
    <iframe
      key={src}
      src={src}
      title="Vista previa del documento"
      className="w-full h-full border-0 bg-white"
      onError={() => { setPdfError(true); }}
      onLoad={(e) => {
        // Same-origin check only catches our own error pages; the S3 PDF is
        // cross-origin so contentDocument throws and we assume it loaded.
        try {
          const doc = e.currentTarget.contentDocument;
          if (doc && doc.body.innerText === '') setPdfError(true);
        } catch {
          // Cross-origin — cannot inspect, assume OK.
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Adjunto Item
// ---------------------------------------------------------------------------

interface AdjuntoItemProps {
  adjunto: AdjuntoWithUrl;
  readonly: boolean;
  onPreview: (adjunto: AdjuntoWithUrl) => void;
  onDeleteRequest: () => void;
}

function AdjuntoItem({
  adjunto,
  readonly,
  onPreview,
  onDeleteRequest,
}: AdjuntoItemProps) {
  const canPreview = adjunto.mimeType === 'application/pdf' || adjunto.mimeType.startsWith('image/');

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl',
        'bg-sime-surface-raised border border-sime-border-subtle',
        'hover:border-sime-accent/30 transition-colors',
      )}
    >
      {/* Icon */}
      <div className="shrink-0">
        <FileIcon mimeType={adjunto.mimeType} />
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-sime-text-primary truncate">
          {adjunto.nombre}
        </p>
        <div className="flex items-center gap-2 text-xs text-sime-text-tertiary">
          <span className="px-1.5 py-0.5 rounded bg-sime-surface text-[10px] font-medium uppercase">
            {tipoLabel(adjunto.tipo)}
          </span>
          <span>{formatFileSize(adjunto.size)}</span>
          <span>{new Date(adjunto.createdAt).toLocaleDateString('es-CL')}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {canPreview ? (
          adjunto.mimeType === 'application/pdf' ? (
            <>
              {/* Desktop: in-app iframe viewer (built-in PDF plugin). */}
              <button
                onClick={() => { onPreview(adjunto); }}
                className="hidden md:inline-flex p-1.5 rounded-lg text-sime-text-secondary hover:text-sime-accent transition-colors"
                aria-label={`Ver ${adjunto.nombre}`}
              >
                <Eye className="h-4 w-4" />
              </button>
              {/* Mobile: open in a new tab. iOS Safari and Android Chrome cannot
                  render PDFs inside an iframe, so the in-app viewer shows blank;
                  a top-level navigation triggers the OS-native PDF viewer. */}
              <a
                href={adjunto.url}
                target="_blank"
                rel="noopener noreferrer"
                className="md:hidden inline-flex p-1.5 rounded-lg text-sime-text-secondary hover:text-sime-accent transition-colors"
                aria-label={`Ver ${adjunto.nombre} en pestana nueva`}
              >
                <Eye className="h-4 w-4" />
              </a>
            </>
          ) : (
            <button
              onClick={() => { onPreview(adjunto); }}
              className="p-1.5 rounded-lg text-sime-text-secondary hover:text-sime-accent transition-colors"
              aria-label={`Ver ${adjunto.nombre}`}
            >
              <Eye className="h-4 w-4" />
            </button>
          )
        ) : null}

        {/* downloadUrl carries Content-Disposition: attachment, so S3 forces the
            download even cross-origin (the `download` attr below is ignored
            cross-origin and only helps in the same-origin fallback case). The
            view paths above keep using `url` (inline) so PDFs/images render. */}
        <a
          href={adjunto.downloadUrl}
          download={adjunto.nombre}
          className="p-1.5 rounded-lg text-sime-text-secondary hover:text-sime-accent transition-colors"
          aria-label={`Descargar ${adjunto.nombre}`}
        >
          <Download className="h-4 w-4" />
        </a>

        {!readonly ? (
          <button
            type="button"
            onClick={onDeleteRequest}
            className="p-1.5 rounded-lg text-sime-text-tertiary hover:text-sime-danger transition-colors"
            aria-label={`Eliminar ${adjunto.nombre}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </motion.div>
  );
}
